// A topic's review screen: the objective and the list, read-only, who is in the topic and who
// is about to be invited, and the one action that fits the reader (D150).
//
// What is offered depends on who the reader is, all read from the topic and the people list
// the server sent:
//
//   * A principal with people to invite: one button. Principals are invited before the
//     family, so while any principal is waiting the button names only them. The press also
//     records the reader's own finish, on the server.
//   * A principal with nobody assigned: no button, and where to add people.
//   * A principal whose family invitation waits on another principal: who it waits for.
//   * Someone arriving after others: "Join the others".
//   * Anyone else: no button, and who they are with.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import { MOVE_ACTION, type Invite, type TopicView } from '../api/contract.js';
import { nameList, relationLabel } from '../format.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { Internals } from '../internals.js';
import type { PlanState } from '../plan/usePlan.js';
import { Decisions } from '../surfaces/Decisions.js';
import { hueOf, initialsOf } from './PersonRow.js';
import { InlineForm } from './RowEdit.js';

export type TopicReviewPageProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
  topic: TopicView;
};

const firstName = (name: string): string => name.split(' ')[0] ?? name;

type Person = {
  name: string;
  you: boolean;
  relation?: string | undefined;
  invited?: boolean;
  onAddAddress?: (() => void) | undefined;
};

function PeopleRow({ label, people }: { label?: string; people: Person[] }): JSX.Element {
  return (
    <div className="review__row">
      {label ? <h4 className="review__rowLabel">{label}</h4> : null}
      <ul className="review__rowPeople">
        {people.map((person) => (
          <li
            key={`${person.you ? 'you' : 'other'}-${person.name}`}
            className="review__person"
          >
            <span
              className={`person__avatar person__avatar--${hueOf(person.name)}`}
              aria-hidden="true"
            >
              {initialsOf(person.name)}
            </span>
            <span className="review__personIdentity">
              <span className="review__personName">
                {person.you ? 'You' : firstName(person.name)}
              </span>
              {person.relation === undefined ? null : (
                <span className="review__personRelation">{person.relation}</span>
              )}
            </span>
            <span className="review__personStatus">
              {person.invited === true ? 'Invited' : ''}
              {person.onAddAddress === undefined ? null : (
                <button
                  type="button"
                  className="review__addAddress"
                  onClick={person.onAddAddress}
                >
                  Add email address
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TopicReviewPage({
  state,
  internals,
  client,
  planId,
  topic,
}: TopicReviewPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProblem, setDownloadProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [addressFor, setAddressFor] = useState<string | null>(null);
  const [addressProblem, setAddressProblem] = useState<string | null>(null);
  if (snapshot === null) {
    return null;
  }

  const self = snapshot.people.find((person) => person.self);
  const memberIds = new Set(topic.members.map((member) => member.id));
  const othersIn = topic.members.filter((member) => member.id !== self?.id);
  const planning = viewerIsPrincipal(snapshot);
  const assigned = snapshot.people.filter(
    (person) => !person.self && person.topics.includes(topic.key),
  );
  const principalsToInvite = assigned.filter(
    (person) => person.sameSay && !memberIds.has(person.id),
  );
  const familyToInvite = assigned.filter(
    (person) => !person.sameSay && !memberIds.has(person.id),
  );

  // The family request, when one is open here and the reader has already confirmed it.
  const confirmedRequest =
    self === undefined
      ? undefined
      : snapshot.pendingActions.find(
          (action) =>
            action.type === MOVE_ACTION &&
            action.topic === topic.key &&
            action.acceptedBy.includes(self.id),
        );
  const waitingOn =
    confirmedRequest === undefined
      ? []
      : topic.members.filter(
          (member) =>
            member.id !== self?.id &&
            snapshot.people.some((person) => person.id === member.id && person.sameSay) &&
            !confirmedRequest.acceptedBy.includes(member.id),
        );

  const run = async (
    call: () => Promise<TopicView>,
    after?: () => void,
  ): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await call();
      state.refetch();
      after?.();
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t go through. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  const saveAddress = async (person: string, email: string): Promise<void> => {
    setBusy(true);
    setAddressProblem(null);
    try {
      await client.changeRecord(planId, { kind: 'set_person_email', person, email });
      state.refetch();
      setAddressFor(null);
    } catch (error) {
      setAddressProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t save. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  // Fetched rather than linked, because the route is bearer-authenticated and an anchor
  // carries no header. A 503 means the renderer is not configured.
  const download = async (): Promise<void> => {
    setDownloading(true);
    setDownloadProblem(null);
    try {
      const { blob, filename } = await client.downloadArtifact(planId, topic.key);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadProblem(
        error instanceof ApiFailure && error.code === 'artifact_unavailable'
          ? 'A copy can’t be made just now. Everything here is still here.'
          : 'That didn’t download. Try again.',
      );
    } finally {
      setDownloading(false);
    }
  };

  const invite = (who: Invite): Promise<void> =>
    run(() => client.invite(planId, topic.key, who));

  const objective =
    snapshot.objectives.find((item) => item.topic === topic.key && item.text !== null)
      ?.text ?? null;
  const relationOf = (id: string): string | undefined => {
    const relation = snapshot.people.find((person) => person.id === id)?.relation;
    return relation === null || relation === undefined
      ? undefined
      : relationLabel(relation);
  };
  const inNow: Person[] = [
    { name: self?.name ?? 'You', you: true },
    ...othersIn.map((member) => ({
      name: member.name,
      you: false,
      relation: relationOf(member.id),
      invited: !member.finished,
    })),
  ];
  const toInvite = [...principalsToInvite, ...familyToInvite];
  // Who the next press of Invite would cover, and which of them have no address to write to.
  const covered = principalsToInvite.length > 0 ? principalsToInvite : familyToInvite;
  const needingAddress = planning
    ? covered.filter((person) =>
        person.sameSay
          ? !person.joined && person.email === null
          : !person.joined && person.email === null && !person.invited,
      )
    : [];
  const addressee = needingAddress.find((person) => person.id === addressFor);
  const familyNames = nameList(familyToInvite.map((person) => firstName(person.name)));
  const unfinished = othersIn.filter((member) => !member.finished);
  const unfinishedNames = nameList(unfinished.map((member) => firstName(member.name)));
  const canJoin = !topic.viewer.finished && othersIn.length > 0;

  // The footer under the People card holds a note on the left and, when there is one, the
  // button on the right.
  let action: JSX.Element | null = null;
  let note: JSX.Element | string | null = null;
  let cardNote: string | null = null;
  let cardAction: JSX.Element | null = null;
  let subtitle: string | null = null;
  if (planning && confirmedRequest !== undefined) {
    subtitle =
      waitingOn.length === 0
        ? 'The others still have to confirm the invitation.'
        : `${nameList(waitingOn.map((member) => firstName(member.name)))} ${waitingOn.length === 1 ? 'still has' : 'still have'} to confirm the invitation.`;
    cardNote =
      waitingOn.length === 0
        ? 'Waiting for the others.'
        : `Waiting for ${nameList(waitingOn.map((member) => firstName(member.name)))}.`;
  } else if (planning && toInvite.length > 0) {
    const principalsFirst = principalsToInvite.length > 0;
    const named = principalsFirst ? principalsToInvite : familyToInvite;
    subtitle = `When it is ready for ${nameList(named.map((person) => firstName(person.name)))} to see, invite them.`;
    if (principalsFirst && familyToInvite.length > 0) {
      cardNote = `${familyNames} will be invited once every principal has finished their part.`;
    }
    if (needingAddress.length > 0) {
      cardNote = `Add an email address for ${nameList(needingAddress.map((person) => firstName(person.name)))} to invite them.`;
    }
    cardAction = (
      <button
        type="button"
        className="button button--primary"
        disabled={busy || needingAddress.length > 0}
        onClick={() =>
          void invite(
            principalsFirst
              ? { people: principalsToInvite.map((person) => person.id) }
              : { who: 'family' },
          )
        }
      >
        Invite
      </button>
    );
  } else if (canJoin) {
    subtitle = `When you have said what you want to here, join ${nameList(othersIn.map((member) => firstName(member.name)))} in a conversation about all of it together.`;
    action = (
      <button
        type="button"
        className="button button--primary"
        disabled={busy}
        onClick={() =>
          void run(
            () => client.finishTopic(planId, topic.key),
            () => navigate(`/plans/${planId}/topics/${topic.key}`),
          )
        }
      >
        Join the others
      </button>
    );
  } else if (
    planning &&
    topic.viewer.finished &&
    toInvite.length === 0 &&
    unfinished.length > 0
  ) {
    subtitle =
      unfinished.length === 1
        ? `${unfinishedNames} has been invited. ${unfinishedNames} will review the list privately with Mor, then you will both talk together. You can keep adding to the list in the meantime.`
        : `${unfinishedNames} have been invited. Each will review the list privately with Mor, then everyone will talk together. You can keep adding to the list in the meantime.`;
  } else if (planning && assigned.length === 0 && othersIn.length === 0) {
    subtitle = 'Nobody else is in this topic yet.';
    note = (
      <>
        Tell Mor who should be part of it, or add people on{' '}
        <Link to={`/plans/${planId}/people`}>the family page</Link>.
      </>
    );
  }

  return (
    <section className="page">
      <Link className="page__back" to={`/plans/${planId}/topics/${topic.key}`}>
        ‹ Back to the list
      </Link>
      <h2 className="page__title">Review your work</h2>
      {subtitle === null ? null : <p className="page__subtitle">{subtitle}</p>}
      {objective === null ? null : (
        <>
          <h3 className="decisions__objective-title">What this is for</h3>
          <p className="decisionsPage__objective">{objective}</p>
        </>
      )}
      <h3 className="sectionLabel">Questions</h3>
      <Decisions
        state={state}
        internals={internals}
        client={client}
        planId={planId}
        topic={topic}
        readOnly
      />
      <div className="review__people">
        <h3 className="review__cardTitle">People</h3>
        <PeopleRow people={inNow} />
        {toInvite.length === 0 ? null : (
          <PeopleRow
            label="To be invited"
            people={toInvite.map((person) => ({
              name: person.name,
              you: false,
              relation: relationOf(person.id),
              onAddAddress: needingAddress.some((other) => other.id === person.id)
                ? () => {
                    setAddressProblem(null);
                    setAddressFor(person.id);
                  }
                : undefined,
            }))}
          />
        )}
        {addressee === undefined ? null : (
          <InlineForm
            key={addressee.id}
            label="Email"
            email
            initial=""
            busy={busy}
            error={addressProblem}
            onCancel={() => setAddressFor(null)}
            onSave={(email) => saveAddress(addressee.id, email)}
          />
        )}
        {cardNote === null && cardAction === null ? null : (
          <div className="review__peopleFoot">
            {cardNote === null ? null : <p className="review__peopleNote">{cardNote}</p>}
            {cardAction}
          </div>
        )}
      </div>
      {note === null && action === null ? null : (
        <div className="page__foot">
          {note === null ? null : <p className="page__footNote">{note}</p>}
          {action}
        </div>
      )}
      {problem === null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
      <div className="topicList__download">
        <button
          type="button"
          className="artifact__link"
          disabled={downloading}
          onClick={() => void download()}
        >
          Download a copy
        </button>
      </div>
      {downloadProblem === null ? null : (
        <p className="artifact__problem" role="status">
          {downloadProblem}
        </p>
      )}
    </section>
  );
}
